import { forwardRef, useEffect, useMemo, useRef } from 'react'
import { Switch } from '@headlessui/react'
import { isUndefined } from 'lodash'
import { sanitizeText } from '../../helpers/utils'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const getMatchesDefaultCondition = (item, product) => {
    const defaultCondition = item?.defaultCondition

    if (!defaultCondition) return Boolean(item?.default)
    if (defaultCondition.type !== 'product') return false

    const slug = product?.slug || ''
    const valuesToMatch = Array.isArray(defaultCondition.value)
        ? defaultCondition.value
        : [defaultCondition.value]

    return valuesToMatch.includes(slug)
}

const OfferUpgradeField = forwardRef(function OfferUpgradeField(props, ref) {
    const { item, setValue, getValues, watch, product, errors, shouldReset } =
        props

    const label = item?.label || item?.name
    const options = Array.isArray(item?.options) ? item.options : []
    const noOption = options[0] || "No, don't add offer"
    const enabledOptions = options.slice(1)
    const watchedValue = watch ? watch(item.name) : getValues?.(item.name)
    const selectedValue = isUndefined(watchedValue) ? item?.value : watchedValue
    const selectedEnabledValue = enabledOptions.includes(selectedValue)
        ? selectedValue
        : null
    const isEnabled = Boolean(selectedEnabledValue)
    const isDisabled = Boolean(item?.disabled)
    const hasError = Boolean(errors?.[item.name])
    const tooltipId = item?.name
        ? `${sanitizeText(item.name)}-tooltip`
        : undefined
    const lastEnabledValue = useRef(
        selectedEnabledValue ||
            (getMatchesDefaultCondition(item, product) && item?.default) ||
            enabledOptions[0],
    )

    useEffect(() => {
        if (selectedEnabledValue) {
            lastEnabledValue.current = selectedEnabledValue
        }
    }, [selectedEnabledValue])

    const defaultEnabledValue = useMemo(() => {
        if (getMatchesDefaultCondition(item, product) && item?.default) {
            return item.default
        }

        return enabledOptions[0]
    }, [enabledOptions, item, product])

    const noteText =
        selectedEnabledValue === 'Yes, use my offer values'
            ? "Include an offer amount for each record in your list. Map that column at upload and we'll print it as provided."
            : item?.noteText

    useEffect(() => {
        if (!item?.name || !setValue || options.length < 1) return

        const currentValue = getValues?.(item.name)
        if (!isUndefined(currentValue)) return

        const initialValue =
            shouldReset || !getMatchesDefaultCondition(item, product)
                ? noOption
                : defaultEnabledValue

        setValue(item.name, initialValue)
    }, [defaultEnabledValue, getValues, item, noOption, options.length, product, setValue, shouldReset])

    const handleToggleChange = (nextEnabled) => {
        if (!setValue || !item?.name) return

        if (nextEnabled) {
            const nextValue = lastEnabledValue.current || defaultEnabledValue
            setValue(item.name, nextValue)
            return
        }

        setValue(item.name, noOption)
    }

    const handleSelectChange = (event) => {
        const nextValue = event.target.value
        lastEnabledValue.current = nextValue
        setValue(item.name, nextValue)
    }

    const renderInfoTooltip = () => {
        if (!item?.tooltipText) return null

        return (
            <button
                type="button"
                tabIndex={0}
                aria-label={`More information about ${label}`}
                aria-describedby={tooltipId || undefined}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                className="group/info relative inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] border-gray-400 bg-transparent p-0 font-serif text-[10px] font-bold italic leading-none text-gray-400 hover:border-[#ED5C2F] hover:text-[#ED5C2F] focus:border-[#ED5C2F] focus:text-[#ED5C2F] focus:outline-none">
                i
                <span
                    id={tooltipId || undefined}
                    role="tooltip"
                    className="pointer-events-none absolute bottom-[150%] right-[-3px] z-[60] w-[230px] rounded-lg bg-[#1A1D23] px-3 py-2 text-left font-sans text-[11.5px] font-medium not-italic leading-[1.45] text-white opacity-0 shadow-[0_12px_34px_rgba(26,29,35,0.18)] transition-opacity duration-150 group-hover/info:opacity-100 group-focus/info:opacity-100">
                    {item.tooltipText}
                    <span className="absolute right-[9px] top-full h-0 w-0 border-[5px] border-transparent border-t-[#1A1D23]" />
                </span>
            </button>
        )
    }

    const switchClassName = classNames(
        isDisabled ? 'cursor-not-allowed opacity-70' : '',
        isEnabled
            ? 'bg-[linear-gradient(90deg,#ED5C2F,#F28230)]'
            : 'bg-[#D9D2C6]',
        'relative inline-flex h-[26px] w-[46px] flex-shrink-0 items-center rounded-full border-none p-[3px] transition-colors duration-200',
    )

    const switchKnobClassName = classNames(
        isEnabled ? 'translate-x-5' : 'translate-x-0',
        'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transform transition duration-200 ease-in-out',
    )

    return (
        <Switch.Group
            as="div"
            ref={ref}
            className={classNames(
                hasError ? 'border-red-500' : '',
                isDisabled ? 'opacity-70' : '',
                isEnabled
                    ? 'border-[#F6D9C2] shadow-[0_0_0_3px_rgba(242,130,48,0.08)]'
                    : 'border-[#E1DACE]',
                'mb-2 rounded-xl border bg-white px-4 py-3 transition-all duration-150',
            )}>
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Switch.Label
                            as="span"
                            className="text-sm font-semibold leading-5 text-olm-black"
                            passive>
                            {label}
                        </Switch.Label>
                        {item?.tagLabel && (
                            <span className="inline-flex items-center rounded-[5px] bg-[#EEF3FF] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#3B6FD4]">
                                {item.tagLabel}
                            </span>
                        )}
                        {renderInfoTooltip()}
                    </div>
                </div>
                <Switch
                    data-switch-id={`${sanitizeText(item?.name)}`}
                    checked={isEnabled}
                    onChange={handleToggleChange}
                    disabled={isDisabled}
                    className={switchClassName}>
                    <span aria-hidden="true" className={switchKnobClassName} />
                </Switch>
            </div>

            {isEnabled && (
                <div className="mt-3 border-t border-dashed border-[#E1DACE] pt-3">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">
                        {item?.optionsLabel || 'Offer values'}
                    </label>
                    <div className="relative">
                        <select
                            value={selectedEnabledValue || defaultEnabledValue}
                            onChange={handleSelectChange}
                            disabled={isDisabled}
                            style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                backgroundImage: 'none',
                            }}
                            className={classNames(
                                hasError
                                    ? 'border-red-500'
                                    : 'border-[#E1DACE]',
                                isDisabled
                                    ? 'bg-gray-100 cursor-not-allowed opacity-70'
                                    : 'bg-white',
                                'w-full appearance-none rounded-[10px] border px-3.5 py-[13px] pr-10 text-[14.5px] font-medium text-olm-black transition-all duration-150 focus:border-[#F28230] focus:outline-none focus:ring-4 focus:ring-[#F28230]/15',
                            )}>
                            {enabledOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-gray-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2"
                                aria-hidden="true">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </span>
                    </div>
                    {noteText && (
                        <p className="mt-3 rounded-lg border border-[#F6D9C2] bg-[#FDF4EC] px-3 py-2 text-[12px] leading-5 text-gray-500">
                            {noteText}
                        </p>
                    )}
                </div>
            )}
        </Switch.Group>
    )
})

export default OfferUpgradeField
